import { activateUserAccount } from "../api/api";
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import config from '../utils/config'; // Adjust import path as needed

const Activation = () => {
    const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
    const [message, setMessage] = useState('');
    const { token } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const activateAccount = async () => {
            try {
                const response = await activateUserAccount(token);

                if (response.responsecode == 200) {
                    setStatus('success');
                    setMessage(response.message);
                    
                    // Redirect to login after 3 seconds
                    setTimeout(() => {
                        navigate('/login');
                    }, 3000);
                } else if(response.responsecode != 200) {
                    setStatus('error');
                    setMessage(response.message);
                }
            } catch (error) {
                setStatus('error');
                setMessage('Network error. Please try again.');
            }
        };

        if (token) {
            activateAccount();
        }
    }, [token, navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center border border-gray-100">
                {/* Logo Section */}
                <div className="mb-8">
                    <img 
                        src={`${config.IMG_URL}/tuly.png`}
                        alt="Tuly" 
                        className="h-16 w-auto mx-auto mb-4"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='200' height='50' viewBox='0 0 200 50'%3e%3ctext x='0' y='40' font-family='Arial' font-size='40' font-weight='bold' fill='blue'%3eTuly%3c/text%3e%3c/svg%3e";
                        }}
                    />
                </div>

                {/* Content based on status */}
                {status === 'loading' && (
                    <div className="space-y-6">
                        <div className="relative">
                            <div className="absolute inset-0 bg-blue-100 rounded-full animate-pulse"></div>
                            <Loader size={64} className="relative text-blue-600 animate-spin mx-auto" />
                        </div>
                        <div className="space-y-3">
                            <h2 className="text-2xl font-bold text-gray-800">Activating Your Account</h2>
                            <p className="text-gray-600 leading-relaxed">
                                Please wait while we verify your account...
                            </p>
                        </div>
                        <div className="flex justify-center">
                            <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            </div>
                        </div>
                    </div>
                )}
                
                {status === 'success' && (
                    <div className="space-y-6">
                        <div className="relative">
                            {/*<div className="absolute inset-0 bg-green-100 rounded-full animate-pulse"></div>*/}
                            <CheckCircle size={64} className="relative text-green-600 mx-auto" />
                        </div>
                        <div className="space-y-3">
                            <h2 className="text-2xl font-bold text-green-800">Account Activated!</h2>
                            <p className="text-gray-600 leading-relaxed">{message}</p>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <p className="text-sm text-green-700 font-medium">
                                🎉 Welcome to Tuly!
                            </p>
                            <p className="text-sm text-green-600 mt-1">
                                Redirecting to login page in a few seconds...
                            </p>
                        </div>
                        <div className="flex justify-center">
                            <div className="bg-green-600 h-1 w-full rounded-full overflow-hidden">
                                <div className="bg-green-400 h-full animate-pulse"></div>
                            </div>
                        </div>
                    </div>
                )}
                
                {status === 'error' && (
                    <div className="space-y-6">
                        <div className="relative">
                            {/*<div className="absolute inset-0 bg-red-100 rounded-full"></div>*/}
                            <XCircle size={64} className="relative text-red-600 mx-auto" />
                        </div>
                        <div className="space-y-3">
                            <h2 className="text-2xl font-bold text-red-800">Activation Failed</h2>
                            <p className="text-gray-600 leading-relaxed">{message}</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-red-700 font-medium">
                                Don't worry, this can happen sometimes.
                            </p>
                            <p className="text-sm text-red-600 mt-1">
                                Please try signing up again or contact support if the problem persists.
                            </p>
                        </div>
                        <div className="space-y-3">
                            {/*<button 
                                onClick={() => navigate('/signup')}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg py-3 px-6 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl">
                                Try Again
                            </button>*/}
                            <button 
                                onClick={() => navigate('/login')}
                                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg py-3 px-6 transition-colors">
                                Go to Login
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Activation;